// Wire-format types for the My Profile feature (PRD 20 — FEATURE_052/053).
// Mirrors the backend kr.co.ards.admin.profile DTOs + ErrorCode catalog.

export interface MyProfile {
  id: number;
  email: string;
  name: string;
  // Always populated post-signup (PRD 20 §4.1). null tolerated only for
  // legacy rows; FE renders "-" when null. Backend sends digits (01012345678);
  // the FE formats to 010-XXXX-XXXX for display.
  phone: string | null;
  institution: string;
  // 0..N non-root roles (PRD 15 §4.1). Root sees [] + isRoot=true.
  roles: string[];
  isRoot: boolean;
  joinedAt: string; // ISO (KST) — card 1 가입일
  passwordChangedAt: string | null; // ISO (KST) — card 2 "마지막 변경"
}

// ── Phone change flow (PRD 20 §3.3) ──────────────────────────────────
// Backend = 2 endpoints: request-otp sends the SMS; verify checks the code AND
// commits the new number. The prototype's [인증하기] is a CLIENT-side 6-digit
// format check (no call); [전화번호 저장] triggers the verify-commit below.

export interface PhoneSendCodeInput {
  // Digits-only (01012345678). Maps to the backend `phone` field.
  newPhone: string;
}

export interface PhoneSaveInput {
  // The new number + the 6-digit SMS code. Backend verifies the code and, on
  // success, commits the number in the same call (returns the updated profile).
  newPhone: string;
  code: string;
}

// ── Password change flow (PRD 20 §3.4) ────────────────────────────────

export interface PasswordChangeInput {
  currentPassword: string;
  newPassword: string;
  // Sent to the backend, which re-validates the match (defense-in-depth).
  confirmPassword: string;
}

// Response body of POST /my-profile/password (FEATURE_053 session continuity). Changing the Firebase
// password invalidates the FE's current ID token, so the backend returns a fresh custom token; the adapter
// exchanges it via signInWithCustomToken BEFORE the my-profile refetch so the session survives (no logout).
export interface PasswordChangeResult {
  passwordChangedAt: string;
  accessToken?: string; // absent in the mock branch (no real Firebase session)
}

// ── Errors ────────────────────────────────────────────────────────────
// The backend wire codes (ErrorCode.java). The FE maps each → a Korean i18n key.

export type MyProfileApiErrorCode =
  // Phone change
  | 'PHONE_INVALID' // blank / malformed new number ("cannot blank phone")
  | 'PHONE_DUPLICATE' // new number already used by another admin (uq_ad_admin_phone backstop)
  | 'OTP_RATE_LIMITED' // 60s resend cooldown OR verify brute-force cap
  | 'SMS_SEND_FAILED'
  | 'OTP_INVALID' // wrong 6-digit code (at verify-commit)
  | 'OTP_EXPIRED' // code expired / never sent
  // Password change
  | 'PASSWORD_INVALID' // wrong current password (reauth)
  | 'PASSWORD_POLICY_VIOLATION' // new password fails 8–64 + letter+digit+symbol
  | 'PASSWORD_CONFIRM_MISMATCH' // new ≠ confirm (server backstop)
  | 'PASSWORD_REUSED' // new equals current or one of last 3 (no-reuse-3)
  // Cross-cutting
  | 'UNAUTHORIZED'
  | 'UNKNOWN';

export interface MyProfileApiErrorMeta {
  // Backend may carry a cooldown remainder on a 429; FE falls back to 60s.
  retryAfterSeconds?: number;
}

export class MyProfileApiError extends Error {
  readonly code: MyProfileApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: MyProfileApiErrorMeta;

  constructor(
    code: MyProfileApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: MyProfileApiErrorMeta },
  ) {
    super(message);
    this.name = 'MyProfileApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, MyProfileApiError.prototype);
  }
}

export interface MyProfileApi {
  // GET /api/v1/admin/my-profile
  getMe(): Promise<MyProfile>;
  // POST /api/v1/admin/my-profile/phone/request-otp — 204
  sendPhoneCode(input: PhoneSendCodeInput): Promise<void>;
  // POST /api/v1/admin/my-profile/phone/verify — verify-commit, returns updated profile
  savePhone(input: PhoneSaveInput): Promise<MyProfile>;
  // POST /api/v1/admin/my-profile/password — 200 { passwordChangedAt, accessToken }; the adapter exchanges
  // accessToken via signInWithCustomToken to keep the session (no logout). Resolves void to callers.
  changePassword(input: PasswordChangeInput): Promise<void>;
}
